import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next'

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-slate-500 mt-1.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent, sub, testid, delay = 0, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      data-testid={testid}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? label : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={`bg-white border border-slate-200 rounded-sm p-6 shadow-[0_8px_30px_rgba(11,37,69,0.05)] hover:-translate-y-[2px] transition-transform ${clickable ? "cursor-pointer hover:border-[#C5A059] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-2" : ""}`}
    >
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
        {Icon && (
          <span
            className="w-9 h-9 rounded-sm flex items-center justify-center"
            style={{ backgroundColor: accent ? "#C5A05918" : "#0B254510" }}
          >
            <Icon size={18} className={accent ? "text-[#C5A059]" : "text-[#0B2545]"} weight="bold" aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="mt-4 font-serif text-3xl text-slate-900">{value}</div>
      {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
    </motion.div>
  );
}

const STATUS_COLORS = {
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Reserved: "bg-amber-50 text-amber-700 border-amber-200",
  "Under negotiation": "bg-blue-50 text-blue-700 border-blue-200",
  Sold: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_I18N_KEYS = {
  Available: "places.available",
  Reserved: "places.reserved",
  "Under negotiation": "places.underNegotiation",
  Sold: "places.sold",
};

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  const label = STATUS_I18N_KEYS[status] ? t(STATUS_I18N_KEYS[status], status) : status;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium border rounded-sm ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {label}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {Icon && <Icon size={44} className="text-slate-300 mb-4" aria-hidden="true" />}
      <h3 className="font-serif text-xl text-slate-700">{title}</h3>
      {subtitle && <p className="text-slate-400 mt-1 max-w-sm">{subtitle}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function DemoBadge() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-[#C5A059]/15 text-[#8a6d2f] border border-[#C5A059]/30 rounded-sm">
      {t('sample', 'Sample')}
    </span>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{children ?? "—"}</div>
    </div>
  );
}
